import { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db, addAuditLog } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import styles from '../admin/TrackingPage.module.css';
import { formatOrderId } from '../../utils/formatOrderId';

const STATUS_OPTIONS = ['Pending', 'In Transit', 'Delivered'];
const normalizeStatus = s => s === 'On its way' ? 'In Transit' : s;

const STAGES = ['Order Placed', 'Confirmed', 'In Transit', 'Delivered'];
const STAGE_INDEX = {
  'Pending':    0,
  'Confirmed':  1,
  'In Transit': 2,
  'On its way': 2,
  'Delivered':  3,
};

function StatusBadge({ status }) {
  const cls = styles[`badge_${status.replace(/\s+/g, '_')}`];
  return <span className={`${styles.badge} ${cls ?? ''}`}>{status}</span>;
}

function StatCard({ label, value, accent }) {
  return (
    <div className={styles.statCard}>
      <span className={`${styles.statValue} ${accent ? styles.statAccent : ''}`}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

function ProgressStepper({ status }) {
  const current = STAGE_INDEX[status] ?? 0;
  return (
    <div style={{ display: 'flex', gap: 0, alignItems: 'center', padding: '16px 24px' }}>
      {STAGES.map((stage, i) => {
        const done   = i < current;
        const active = i === current;
        return (
          <div key={stage} style={{ display: 'flex', alignItems: 'center', flex: i < STAGES.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: done ? 'var(--success)' : active ? 'var(--brown-dark)' : 'var(--cream-dark)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: done || active ? '#fff' : 'var(--text-muted)',
                fontSize: 14, fontWeight: 700,
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: active ? 'var(--brown-dark)' : done ? 'var(--success)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {stage}
              </span>
            </div>
            {i < STAGES.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? 'var(--success)' : 'var(--cream-dark)', margin: '0 8px', marginBottom: 20 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-PH', { hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatTotal(total) {
  if (typeof total === 'number') return `₱${total.toLocaleString()}`;
  return total ?? '—';
}

export default function SupplierTracking() {
  const { user }                            = useAuth();
  const [deliveries, setDeliveries]         = useState([]);
  const [expanded, setExpanded]             = useState(null);
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [saving, setSaving]                 = useState({});

  useEffect(() => {
    const q = query(collection(db, 'orders'), orderBy('updatedAt', 'desc'));
    return onSnapshot(q, snap => {
      setDeliveries(snap.docs.map(d => {
        const data = d.data();
        return { _docId: d.id, ...data, status: normalizeStatus(data.status) };
      }));
    });
  }, []);

  function handleSelectChange(id, newStatus) {
    setPendingUpdates(prev => ({ ...prev, [id]: newStatus }));
  }

  async function applyUpdate(delivery) {
    const newStatus = pendingUpdates[delivery._docId];
    if (!newStatus || saving[delivery._docId]) return;

    const eta =
      newStatus === 'Delivered'  ? 'Delivered' :
      newStatus === 'In Transit' ? 'Est. arrival: 2–4 hrs' :
      'Awaiting dispatch';

    setSaving(prev => ({ ...prev, [delivery._docId]: true }));
    try {
      await updateDoc(doc(db, 'orders', delivery._docId), {
        status:           newStatus,
        estimatedArrival: eta,
        updatedAt:        serverTimestamp(),
      });
      await addAuditLog(user.uid, user.name, `Updated delivery ${delivery._docId} to "${newStatus}"`, 'Tracking');
      setPendingUpdates(prev => { const n = { ...prev }; delete n[delivery._docId]; return n; });
    } catch (e) {
      console.error('Update tracking failed:', e);
    } finally {
      setSaving(prev => ({ ...prev, [delivery._docId]: false }));
    }
  }

  const totalActive    = deliveries.filter(d => d.status !== 'Delivered').length;
  const inTransitCount = deliveries.filter(d => d.status === 'In Transit').length;
  const pendingCount   = deliveries.filter(d => d.status === 'Pending').length;
  const deliveredCount = deliveries.filter(d => d.status === 'Delivered').length;

  return (
    <div className={styles.page}>
      <PageHeader title="Supply Tracking" description="Monitor all active deliveries and shipment progress" />

      <div className={styles.statRow}>
        <StatCard label="Total Active Deliveries" value={totalActive} />
        <StatCard label="In Transit"               value={inTransitCount} accent />
        <StatCard label="Pending Dispatch"         value={pendingCount} />
        <StatCard label="Delivered"                value={deliveredCount} />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Order ID</th><th>Branch</th><th>Items</th><th>Total</th><th>Status</th><th>ETA</th><th>Last Updated</th><th>Action</th><th></th>
            </tr>
          </thead>
          <tbody>
            {deliveries.length === 0 ? (
              <tr><td colSpan={9} className={styles.empty}>No tracking records found.</td></tr>
            ) : (
              deliveries.map((d, idx) => (
                <>
                  <tr
                    key={d._docId}
                    className={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}
                  >
                    <td className={styles.orderId}>{formatOrderId(d._docId)}</td>
                    <td className={styles.branch}>{d.branch ?? d.branchId ?? '—'}</td>
                    <td className={styles.items}>{d.details || (d.items || []).map(i => `${i.productId} (${i.qty}x)`).join(', ') || '—'}</td>
                    <td>{formatTotal(d.total)}</td>
                    <td><StatusBadge status={d.status} /></td>
                    <td className={styles.eta}>{d.estimatedArrival || '—'}</td>
                    <td className={styles.dispatch}>{formatDate(d.updatedAt)}</td>
                    <td>
                      {d.status === 'Delivered' ? (
                        <span className={styles.deliveredLock}>✓ Delivered</span>
                      ) : (
                        <div className={styles.actionCell}>
                          <select
                            className={styles.statusSelect}
                            value={pendingUpdates[d._docId] ?? d.status}
                            onChange={e => handleSelectChange(d._docId, e.target.value)}
                          >
                            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <button
                            className={styles.updateBtn}
                            onClick={() => applyUpdate(d)}
                            disabled={!pendingUpdates[d._docId] || pendingUpdates[d._docId] === d.status || saving[d._docId]}
                          >
                            {saving[d._docId] ? '…' : 'Update'}
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <button
                        className={styles.updateBtn}
                        style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--cream-dark)', padding: '4px 10px' }}
                        onClick={() => setExpanded(prev => prev === d._docId ? null : d._docId)}
                      >
                        {expanded === d._docId ? '▲' : '▼'}
                      </button>
                    </td>
                  </tr>
                  {expanded === d._docId && (
                    <tr key={`${d._docId}-stepper`} className={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                      <td colSpan={9}><ProgressStepper status={d.status} /></td>
                    </tr>
                  )}
                </>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
